"use client";

import { type ReactNode, useEffect } from "react";

import { Button } from "@/components/button/button.component";
import { Link } from "@/components/link/link.component";
import { Text } from "@/components/text/text.component";

import { logRouteError } from "@/lib/errors/logRouteError";

export const RouteError = ({
  error,
  reset,
  title,
  description,
  route,
  links = [],
  showRetry = true,
  children,
}: RouteErrorProps) => {
  useEffect(() => {
    void logRouteError({
      message: error.message,
      name: error.name,
      digest: error.digest,
      stack: error.stack,
      route,
    });
  }, [error, route]);

  return (
    <div
      className="flex h-full min-h-full flex-1 flex-col items-center justify-center gap-8 px-4 py-16 lg:py-36"
      role="alert">
      <div className="flex flex-col items-center gap-4">
        <Text variant="h4">{title}</Text>
        <Text variant="subtitle1" className="max-w-prose text-center">
          {description}
        </Text>
      </div>
      <div className="flex flex-col items-center gap-6 lg:flex-row">
        {showRetry ? (
          <Button type="button" secondary onClick={reset}>
            Try again
          </Button>
        ) : null}
        {links.map((link) => (
          <Link
            key={link.href}
            href={link.href}
            secondary={link.secondary ?? true}
            border={link.border}
            className="px-6">
            {link.label}
          </Link>
        ))}
        {children}
      </div>
    </div>
  );
};

export type RouteErrorLink = {
  href: string;
  label: string;
  secondary?: boolean;
  border?: boolean;
};

export type RouteErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
  title: string;
  description: string;
  route?: string;
  links?: RouteErrorLink[];
  showRetry?: boolean;
  children?: ReactNode;
};
